import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../api";
import {
  SpinnerGap, CalendarPlus, ListChecks, Compass, Lightbulb, Sparkle,
  Plus, X, CheckCircle, Star, ArrowsClockwise, GoogleLogo, ArrowRight,
  Trash, Brain, Trophy, Warning, PencilSimple, DotsSixVertical,
} from "@phosphor-icons/react";

const BASE = "";

function gcalUrl(t) {
  // Cria URL Google Calendar template — sem necessidade de OAuth
  if (!t.Data) return null;
  const date = t.Data.toString().substring(0, 10).replaceAll("-", "");
  const hi = (t.HoraInicio || "09:00").toString().substring(0, 5).replace(":", "");
  const hf = (t.HoraFim || t.HoraInicio || "10:00").toString().substring(0, 5).replace(":", "");
  const start = `${date}T${hi}00`;
  const end   = `${date}T${hf}00`;
  return `https://calendar.google.com/calendar/render?action=TEMPLATE`
    + `&text=${encodeURIComponent("[Dreams] " + t.Titulo)}`
    + `&dates=${start}/${end}`
    + `&details=${encodeURIComponent(t.Descricao || "")}`;
}

export default function DreamsSonhoDetail() {
  const { sonhoId } = useParams();
  const [sonho, setSonho] = useState(null);
  const [tarefas, setTarefas] = useState([]);
  const [recursos, setRecursos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [who, setWho] = useState(null);
  const [sim, setSim] = useState(null);
  const [newTask, setNewTask] = useState("");
  const [newRec, setNewRec] = useState({ desc: "", tipo: "T" });
  const [completeTar, setCompleteTar] = useState(null);  // task being completed
  const [editTar, setEditTar] = useState(null);  // null | "new" | task object

  const refresh = async () => {
    const [{ data: s }, { data: t }, { data: r }] = await Promise.all([
      api.get("/sonhos"),
      api.get(`/sonhos/${sonhoId}/tarefas`),
      api.get(`/sonhos/${sonhoId}/recursos`),
    ]);
    const found = (s.data || []).find(x => String(x.ID_Sonho) === String(sonhoId));
    setSonho(found || null);
    setTarefas(t.data || []);
    setRecursos(r.data || []);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { await refresh(); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [sonhoId]);

  const generatePlan = async () => {
    if (!confirm("Gerar um novo plano com IA? As tarefas pendentes actuais serão substituídas.")) return;
    setBusy(true);
    try {
      await api.post(`/sonhos/${sonhoId}/plan/generate`, { replace: true });
      await refresh();
    } finally { setBusy(false); }
  };
  const addTask = async () => {
    if (!newTask.trim()) return;
    await api.post(`/sonhos/${sonhoId}/tarefas`, { titulo: newTask });
    setNewTask("");
    await refresh();
  };
  const delTask = async (id) => {
    if (!confirm("Eliminar esta tarefa?")) return;
    await api.delete(`/tarefas/${id}`);
    await refresh();
  };
  const saveTask = async (payload) => {
    if (editTar && editTar !== "new" && editTar.ID_Tarefa) {
      await api.patch(`/tarefas/${editTar.ID_Tarefa}`, payload);
    } else {
      await api.post(`/sonhos/${sonhoId}/tarefas`, payload);
    }
    setEditTar(null);
    await refresh();
  };
  // ── Drag-and-drop reorder ──
  const [dragId, setDragId] = useState(null);
  const onDragStart = (e, id) => { setDragId(id); e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = async (e, targetId) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const pendents = tarefas.filter(t => t.Estado === "PE");
    const fromIdx = pendents.findIndex(t => t.ID_Tarefa === dragId);
    const toIdx = pendents.findIndex(t => t.ID_Tarefa === targetId);
    if (fromIdx < 0 || toIdx < 0) { setDragId(null); return; }
    const reordered = [...pendents];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    // Optimistic update: tarefas PE pela nova ordem + concluídas no fim mantêm-se
    const concluded = tarefas.filter(t => t.Estado !== "PE");
    setTarefas([...reordered, ...concluded]);
    setDragId(null);
    try {
      await api.patch(`/sonhos/${sonhoId}/tarefas/reorder`,
                      { ordem: reordered.map(t => t.ID_Tarefa) });
    } catch {
      await refresh();
    }
  };
  const addRec = async () => {
    if (!newRec.desc.trim()) return;
    await api.post(`/sonhos/${sonhoId}/recursos`, { descricao: newRec.desc, tipo: newRec.tipo });
    setNewRec({ desc: "", tipo: "T" });
    await refresh();
  };
  const delRec = async (id) => {
    await api.delete(`/recursos/${id}`);
    await refresh();
  };
  const promoteRec = async (id) => {
    if (!confirm("Promover este recurso a sonho?")) return;
    await api.post(`/recursos/${id}/promote`);
    await refresh();
  };
  const loadWho = async () => {
    setBusy(true); setWho({ loading: true });
    try {
      const { data } = await api.post(`/sonhos/${sonhoId}/who`);
      setWho(data?.data || {});
    } catch (e) {
      setWho({ error: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };
  const promoteArea = async (a) => {
    if (!confirm(`Criar novo sonho a partir de "${a.titulo}"?`)) return;
    await api.post(`/sonhos/${sonhoId}/who/promote`, a);
  };
  const loadSim = async () => {
    setBusy(true); setSim({ loading: true });
    try {
      const { data } = await api.post(`/sonhos/${sonhoId}/simulator`);
      setSim(data?.data || []);
    } catch (e) {
      setSim({ error: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  if (loading) return <p className="text-[#978D78] flex items-center gap-2"><SpinnerGap size={16} className="animate-spin" /> A carregar...</p>;
  if (!sonho) return <p className="text-[#978D78]">Sonho não encontrado. <Link to="/dreams/sonhos" className="underline">Voltar à lista</Link></p>;

  return (
    <div className="space-y-10 pb-16">
      {/* Hero */}
      <header className="flex items-start gap-6 flex-wrap">
        <Link to="/dreams/sonhos" className="text-xs text-[#978D78] hover:text-white">← Voltar</Link>
      </header>
      <section className="grid md:grid-cols-5 gap-6 rounded-3xl border border-[#E6E1D6] bg-gradient-to-br from-violet-900/20 to-fuchsia-900/10 p-6">
        <div className="md:col-span-2 relative aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-amber-500/30 to-fuchsia-700/30">
          {sonho.FotoSonhoUrl ? <img src={sonho.FotoSonhoUrl} alt="" className="w-full h-full object-cover" /> : (
            <div className="absolute inset-0 flex items-center justify-center"><Star size={56} weight="fill" className="text-[#6F4F1D]/40" /></div>
          )}
        </div>
        <div className="md:col-span-3 flex flex-col">
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight" data-testid="detail-title">{sonho.Titulo}</h1>
          {sonho.Descricao && <p className="text-[#4B5563] mt-3 text-sm leading-relaxed">{sonho.Descricao}</p>}
          <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
            {sonho.DataInicio && <Info label="Data de início" value={new Date(sonho.DataInicio).toLocaleDateString("pt-PT")} />}
            {sonho.DataAlvo && <Info label="Data alvo" value={new Date(sonho.DataAlvo).toLocaleDateString("pt-PT")} />}
            {sonho.Valor != null && <Info label="Valor" value={`${Number(sonho.Valor).toLocaleString("pt-PT")}€`} />}
            {sonho.Premio && <Info label="Prémio" value={sonho.Premio} icon={Trophy} />}
            {sonho.Consequencia && <Info label="Consequência" value={sonho.Consequencia} icon={Warning} />}
          </div>
        </div>
      </section>

      {/* Plano de Acção */}
      <Section icon={ListChecks} title="Plano de acção"
               action={<button onClick={generatePlan} disabled={busy} data-testid="gen-plan"
                               className="text-xs px-3 py-1.5 rounded-full bg-[#6F4F1D]/15 border border-[#6F4F1D]/30 text-[#6F4F1D] hover:bg-[#6F4F1D]/25 inline-flex items-center gap-1.5">
                         <Sparkle size={11} weight="fill" /> {tarefas.some(t => t.Estado === "PE") ? "Regenerar com IA" : "Gerar com IA"}
                       </button>}>
        {tarefas.length === 0 ? (
          <p className="text-sm text-[#978D78] italic">Sem tarefas. Adiciona manualmente ou gera com IA.</p>
        ) : (
          <ul className="space-y-2">
            {tarefas.map(t => (
              <li key={t.ID_Tarefa} data-testid={`task-${t.ID_Tarefa}`}
                  draggable={t.Estado === "PE"}
                  onDragStart={t.Estado === "PE" ? (e) => onDragStart(e, t.ID_Tarefa) : undefined}
                  onDragOver={t.Estado === "PE" ? onDragOver : undefined}
                  onDrop={t.Estado === "PE" ? (e) => onDrop(e, t.ID_Tarefa) : undefined}
                  className={`rounded-xl border bg-white p-3 flex items-start gap-3 transition ${
                    t.Estado === "CO"
                      ? "opacity-60 border-[#E6E1D6]"
                      : dragId === t.ID_Tarefa
                          ? "border-[#6F4F1D] shadow-md"
                          : "border-[#E6E1D6] hover:border-[#D6CFC0] cursor-grab active:cursor-grabbing"
                  }`}>
                {t.Estado === "PE" && (
                  <span className="text-[#B7AC95] mt-0.5 shrink-0" title="Arraste para reordenar">
                    <DotsSixVertical size={14} />
                  </span>
                )}
                <button onClick={() => t.Estado === "PE" && setCompleteTar(t)}
                        data-testid={`task-toggle-${t.ID_Tarefa}`}
                        disabled={t.Estado !== "PE"}
                        className={`mt-0.5 w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${
                          t.Estado === "CO" ? "bg-emerald-400 border-emerald-400" : "border-[#6F4F1D]/30 hover:bg-[#6F4F1D]/20"
                        }`}>
                  {t.Estado === "CO" && <CheckCircle size={12} weight="fill" className="text-[#0F1829]" />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${t.Estado === "CO" ? "line-through" : ""}`}>{t.Titulo}</p>
                  {t.Descricao && <p className="text-xs text-[#978D78] mt-1">{t.Descricao}</p>}
                  <div className="flex items-center gap-3 mt-1.5 text-[10px] text-[#6F4F1D]/80">
                    {t.Data && <span>{new Date(t.Data).toLocaleDateString("pt-PT", { day: "2-digit", month: "short" })}</span>}
                    {t.HoraInicio && <span>{t.HoraInicio?.toString().substring(0, 5)}{t.HoraFim ? "–" + t.HoraFim.toString().substring(0, 5) : ""}</span>}
                    {t.Data && gcalUrl(t) && (
                      <a href={gcalUrl(t)} target="_blank" rel="noopener noreferrer"
                         data-testid={`task-gcal-${t.ID_Tarefa}`}
                         className="inline-flex items-center gap-1 text-[#978D78] hover:text-[#6F4F1D]">
                        <GoogleLogo size={11} /> Google Cal
                      </a>
                    )}
                    {t.Data && (
                      <a href={`${BASE}/api/dreams/tarefas/${t.ID_Tarefa}.ics`}
                         data-testid={`task-ics-${t.ID_Tarefa}`}
                         className="inline-flex items-center gap-1 text-[#978D78] hover:text-[#6F4F1D]">
                        <CalendarPlus size={11} /> .ics
                      </a>
                    )}
                  </div>
                </div>
                {t.Estado === "PE" && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => setEditTar(t)} title="Editar tarefa"
                            data-testid={`task-edit-${t.ID_Tarefa}`}
                            className="text-[#978D78] hover:text-[#6F4F1D] p-1">
                      <PencilSimple size={13} />
                    </button>
                    <button onClick={() => delTask(t.ID_Tarefa)} title="Eliminar tarefa"
                            data-testid={`task-delete-${t.ID_Tarefa}`}
                            className="text-[#978D78] hover:text-[#B91C1C] p-1">
                      <Trash size={13} />
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        <div className="mt-4 flex gap-2 items-stretch">
          <input value={newTask} onChange={e => setNewTask(e.target.value)}
                 onKeyDown={e => e.key === "Enter" && addTask()}
                 placeholder="Adicionar tarefa rápida..."
                 data-testid="new-task-input"
                 className="flex-1 bg-white border border-[#E6E1D6] rounded-lg px-3 py-2 text-sm" />
          <button onClick={addTask} disabled={!newTask.trim()}
                  data-testid="new-task-add"
                  className="px-4 py-2 rounded-lg bg-[#6F4F1D] text-white text-sm font-semibold disabled:opacity-40 inline-flex items-center gap-1.5">
            <Plus size={12} weight="bold" /> Adicionar
          </button>
          <button onClick={() => setEditTar("new")} title="Adicionar com detalhe (data, hora, descrição)"
                  data-testid="new-task-detail"
                  className="px-3 py-2 rounded-lg bg-white border border-[#E6E1D6] text-[#6F4F1D] text-sm hover:bg-[#F5F2EB] inline-flex items-center gap-1.5">
            <PencilSimple size={12} /> Detalhe
          </button>
        </div>
      </Section>

      {/* Recursos */}
      <Section icon={Compass} title="Recursos">
        <div className="grid sm:grid-cols-2 gap-4">
          <ColumnList title="O que já tens" tipo="T" items={recursos.filter(r => r.Tipo === "T")} onDelete={delRec} />
          <ColumnList title="O que falta" tipo="F" items={recursos.filter(r => r.Tipo === "F")} onDelete={delRec} onPromote={promoteRec} />
        </div>
        <div className="mt-4 flex gap-2">
          <select value={newRec.tipo} onChange={e => setNewRec({ ...newRec, tipo: e.target.value })}
                  data-testid="new-rec-tipo"
                  className="bg-white border border-[#E6E1D6] rounded-lg px-2 py-2 text-sm">
            <option value="T">Tens</option>
            <option value="F">Falta</option>
          </select>
          <input value={newRec.desc} onChange={e => setNewRec({ ...newRec, desc: e.target.value })}
                 onKeyDown={e => e.key === "Enter" && addRec()}
                 placeholder="Ex.: 5000€ de capital / Conhecimento de SQL"
                 data-testid="new-rec-input"
                 className="flex-1 bg-white border border-[#E6E1D6] rounded-lg px-3 py-2 text-sm" />
          <button onClick={addRec} disabled={!newRec.desc.trim()}
                  data-testid="new-rec-add"
                  className="px-4 py-2 rounded-lg bg-[#6F4F1D] text-[#0F1829] text-sm font-semibold disabled:opacity-40 inline-flex items-center gap-1.5">
            <Plus size={12} weight="bold" />
          </button>
        </div>
      </Section>

      {/* Quem precisas de ser */}
      <Section icon={Brain} title="Quem precisas de ser"
               action={!who && <button onClick={loadWho} disabled={busy} data-testid="load-who"
                                       className="text-xs px-3 py-1.5 rounded-full bg-[#6F4F1D]/15 border border-[#6F4F1D]/30 text-[#6F4F1D] hover:bg-[#6F4F1D]/25 inline-flex items-center gap-1.5">
                                 <Sparkle size={11} weight="fill" /> Analisar com IA
                               </button>}>
        {!who && <p className="text-sm text-[#978D78] italic">A IA analisa crenças, valores e metaprogramas que precisas activar.</p>}
        {who?.loading && <p className="text-sm text-[#978D78] flex items-center gap-2"><SpinnerGap size={14} className="animate-spin" /> A pensar...</p>}
        {who?.error && <p className="text-sm text-rose-300">{who.error}</p>}
        {who && !who.loading && !who.error && (
          <div className="grid lg:grid-cols-2 gap-4">
            {who.crencas?.length > 0 && (
              <Card title="Crenças">
                {who.crencas.map((c, i) => (
                  <div key={i} className="mb-2 text-sm">
                    <p className="text-rose-300/80 line-through">{c.actual}</p>
                    <p className="text-emerald-300">→ {c.necessaria}</p>
                  </div>
                ))}
              </Card>
            )}
            {who.valores?.length > 0 && (
              <Card title="Valores a activar">
                <div className="flex flex-wrap gap-1.5">
                  {who.valores.map((v, i) => (
                    <span key={i} className="text-xs px-2 py-1 rounded-full bg-[#6F4F1D]/15 border border-[#6F4F1D]/25 text-[#6F4F1D]">{v}</span>
                  ))}
                </div>
              </Card>
            )}
            {who.metaprogramas?.length > 0 && (
              <Card title="Metaprogramas (PNL)">
                {who.metaprogramas.map((m, i) => (
                  <div key={i} className="mb-2 text-sm">
                    <p className="font-semibold text-[#6F4F1D]">{m.nome} → <span className="text-white">{m.preferido}</span></p>
                    <p className="text-xs text-[#4B5563]">{m.razao}</p>
                  </div>
                ))}
              </Card>
            )}
            {who.areas_melhorar?.length > 0 && (
              <Card title="Áreas a melhorar">
                {who.areas_melhorar.map((a, i) => (
                  <div key={i} className="flex items-start gap-2 mb-2">
                    <div className="flex-1 text-sm">
                      <p className="font-semibold">{a.titulo}</p>
                      <p className="text-xs text-[#4B5563]">{a.descricao}</p>
                    </div>
                    <button onClick={() => promoteArea(a)} data-testid={`promote-area-${i}`}
                            title="Criar sonho a partir desta área"
                            className="text-xs text-[#6F4F1D] hover:text-white inline-flex items-center gap-1">
                      <Plus size={11} /> Sonho
                    </button>
                  </div>
                ))}
              </Card>
            )}
          </div>
        )}
      </Section>

      {/* Simulador */}
      <Section icon={Lightbulb} title="Simulador de oportunidades"
               action={!sim && <button onClick={loadSim} disabled={busy} data-testid="load-sim"
                                       className="text-xs px-3 py-1.5 rounded-full bg-[#6F4F1D]/15 border border-[#6F4F1D]/30 text-[#6F4F1D] hover:bg-[#6F4F1D]/25 inline-flex items-center gap-1.5">
                                 <Sparkle size={11} weight="fill" /> Gerar oportunidades
                               </button>}>
        {!sim && <p className="text-sm text-[#978D78] italic">A IA sugere oportunidades concretas (universidades, empresas, ferramentas) alinhadas com este sonho.</p>}
        {sim?.loading && <p className="text-sm text-[#978D78] flex items-center gap-2"><SpinnerGap size={14} className="animate-spin" /> A pesquisar...</p>}
        {sim?.error && <p className="text-sm text-rose-300">{sim.error}</p>}
        {Array.isArray(sim) && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {sim.map((o, i) => (
              <article key={i} data-testid={`sim-${i}`}
                       className="rounded-xl border border-[#E6E1D6] bg-white/[0.02] p-4 flex flex-col">
                {o.tag && <span className="text-[10px] uppercase tracking-widest text-[#6F4F1D]/80">{o.tag}</span>}
                <h4 className="text-sm font-semibold mt-1">{o.titulo}</h4>
                <p className="text-xs text-[#4B5563] mt-1 flex-1">{o.descricao}</p>
                {o.link && (
                  <a href={o.link} target="_blank" rel="noopener noreferrer"
                     className="text-[11px] text-[#6F4F1D] hover:text-white mt-3 inline-flex items-center gap-1">
                    Visitar <ArrowRight size={11} />
                  </a>
                )}
              </article>
            ))}
          </div>
        )}
        {sim && !sim.loading && !sim.error && (
          <div className="mt-3">
            <button onClick={loadSim} className="text-xs text-[#978D78] hover:text-white inline-flex items-center gap-1">
              <ArrowsClockwise size={11} /> Gerar novamente
            </button>
          </div>
        )}
      </Section>

      {completeTar && <CompleteModal task={completeTar} onClose={() => setCompleteTar(null)} onDone={async () => { setCompleteTar(null); await refresh(); }} />}
      {editTar && <TaskEditor task={editTar === "new" ? null : editTar} onClose={() => setEditTar(null)} onSave={saveTask} />}
    </div>
  );
}

function Info({ label, value, icon: Icon }) {
  return (
    <div className="flex items-start gap-2">
      {Icon && <Icon size={14} className="text-[#6F4F1D] mt-0.5 shrink-0" />}
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-widest text-[#6F4F1D]/80">{label}</p>
        <p className="text-sm text-white truncate">{value}</p>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, action, children }) {
  return (
    <section className="rounded-2xl border border-[#E6E1D6] p-6">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Icon size={18} className="text-[#6F4F1D]" /> {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-xl border border-[#E6E1D6] bg-white/[0.02] p-4">
      <h3 className="text-xs uppercase tracking-widest text-[#6F4F1D]/80 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function ColumnList({ title, tipo, items, onDelete, onPromote }) {
  return (
    <div className={`rounded-xl border p-4 ${tipo === "T" ? "border-emerald-400/30 bg-emerald-400/[0.04]" : "border-[#6F4F1D]/25 bg-[#6F4F1D]/[0.04]"}`}>
      <h3 className={`text-xs uppercase tracking-widest mb-3 ${tipo === "T" ? "text-emerald-300" : "text-[#6F4F1D]"}`}>{title}</h3>
      {items.length === 0 ? (
        <p className="text-xs text-[#978D78] italic">Vazio</p>
      ) : (
        <ul className="space-y-1.5">
          {items.map(r => (
            <li key={r.ID_Recurso} data-testid={`rec-${r.ID_Recurso}`}
                className="flex items-start gap-2 text-sm">
              <span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${tipo === "T" ? "bg-emerald-400" : "bg-[#6F4F1D]"}`} />
              <span className="flex-1">{r.Descricao}</span>
              {onPromote && (
                <button onClick={() => onPromote(r.ID_Recurso)} title="Promover a sonho" className="text-[#6F4F1D] hover:text-white">
                  <Plus size={12} />
                </button>
              )}
              <button onClick={() => onDelete(r.ID_Recurso)} className="text-rose-300/70 hover:text-rose-300">
                <X size={11} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CompleteModal({ task, onClose, onDone }) {
  const [obs, setObs] = useState("");
  const [apr, setApr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/tarefas/${task.ID_Tarefa}/complete`, { observacoes: obs, aprendizagens: apr });
      onDone();
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="complete-modal"
           className="bg-[#F5F2EB] border border-[#E6E1D6] rounded-2xl w-full max-w-md">
        <header className="border-b border-[#E6E1D6] px-6 py-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">Concluir tarefa</h2>
          <button onClick={onClose} className="text-[#978D78] hover:text-white"><X size={18} /></button>
        </header>
        <div className="p-6 space-y-3">
          <p className="text-sm text-[#6F4F1D]">{task.Titulo}</p>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-[#978D78]">Observações</span>
            <textarea value={obs} onChange={e => setObs(e.target.value)} rows={2}
                      data-testid="complete-obs"
                      className="mt-1 w-full bg-white border border-[#E6E1D6] rounded-lg px-3 py-2 text-sm" />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-[#978D78]">Aprendizagens</span>
            <textarea value={apr} onChange={e => setApr(e.target.value)} rows={2}
                      data-testid="complete-apr"
                      className="mt-1 w-full bg-white border border-[#E6E1D6] rounded-lg px-3 py-2 text-sm" />
          </label>
        </div>
        <footer className="border-t border-[#E6E1D6] px-6 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="text-xs text-[#978D78] hover:text-white">Cancelar</button>
          <button onClick={submit} disabled={busy} data-testid="complete-submit"
                  className="px-4 py-2 rounded-full bg-[#6F4F1D] text-[#0F1829] text-sm font-semibold inline-flex items-center gap-1.5">
            {busy ? <SpinnerGap size={12} className="animate-spin" /> : <CheckCircle size={12} weight="fill" />}
            Concluir (+1 ⭐)
          </button>
        </footer>
      </div>
    </div>
  );
}


function TaskEditor({ task, onClose, onSave }) {
  const [titulo, setTitulo] = useState(task?.Titulo || "");
  const [descricao, setDescricao] = useState(task?.Descricao || "");
  const [data, setData] = useState(task?.Data ? String(task.Data).substring(0, 10) : "");
  const [hi, setHi] = useState(task?.HoraInicio ? String(task.HoraInicio).substring(0, 5) : "");
  const [hf, setHf] = useState(task?.HoraFim ? String(task.HoraFim).substring(0, 5) : "");
  const [busy, setBusy] = useState(false);
  const isEdit = !!task;

  const submit = async () => {
    if (!titulo.trim()) return;
    setBusy(true);
    try {
      await onSave({
        titulo: titulo.trim(),
        descricao: descricao.trim() || null,
        data: data || null,
        hora_inicio: hi || null,
        hora_fim: hf || null,
      });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F1829]/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="task-editor-modal"
           className="bg-white border border-[#E6E1D6] rounded-2xl w-full max-w-lg shadow-xl">
        <header className="border-b border-[#E6E1D6] px-6 py-4 flex items-center justify-between">
          <h2 className="text-base font-semibold text-[#0F1829]"
              style={{ fontFamily: "'Fraunces', serif" }}>
            {isEdit ? "Editar tarefa" : "Nova tarefa"}
          </h2>
          <button onClick={onClose} data-testid="task-editor-close"
                  className="text-[#978D78] hover:text-[#0F1829]"><X size={18} /></button>
        </header>
        <div className="p-6 space-y-4">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-[#978D78]">Título *</span>
            <input value={titulo} onChange={e => setTitulo(e.target.value)} autoFocus
                   data-testid="task-editor-titulo"
                   className="mt-1 w-full bg-white border border-[#E6E1D6] rounded-lg px-3 py-2 text-sm" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-[#978D78]">Descrição</span>
            <textarea value={descricao} onChange={e => setDescricao(e.target.value)} rows={3}
                      data-testid="task-editor-descricao"
                      className="mt-1 w-full bg-white border border-[#E6E1D6] rounded-lg px-3 py-2 text-sm" />
          </label>
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#978D78]">Data</span>
              <input type="date" value={data} onChange={e => setData(e.target.value)}
                     data-testid="task-editor-data"
                     className="mt-1 w-full bg-white border border-[#E6E1D6] rounded-lg px-2 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#978D78]">Início</span>
              <input type="time" value={hi} onChange={e => setHi(e.target.value)}
                     data-testid="task-editor-hi"
                     className="mt-1 w-full bg-white border border-[#E6E1D6] rounded-lg px-2 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#978D78]">Fim</span>
              <input type="time" value={hf} onChange={e => setHf(e.target.value)}
                     data-testid="task-editor-hf"
                     className="mt-1 w-full bg-white border border-[#E6E1D6] rounded-lg px-2 py-2 text-sm" />
            </label>
          </div>
        </div>
        <footer className="border-t border-[#E6E1D6] px-6 py-3 flex justify-end gap-2 bg-[#FAF8F3] rounded-b-2xl">
          <button onClick={onClose} data-testid="task-editor-cancel"
                  className="text-xs text-[#978D78] hover:text-[#0F1829] px-3 py-2">Cancelar</button>
          <button onClick={submit} disabled={busy || !titulo.trim()} data-testid="task-editor-save"
                  className="px-4 py-2 rounded-full bg-[#0F1829] text-white text-sm font-semibold disabled:opacity-40 inline-flex items-center gap-1.5 hover:bg-[#1E2A47]">
            {busy ? <SpinnerGap size={12} className="animate-spin" /> : <CheckCircle size={12} weight="fill" />}
            {isEdit ? "Guardar" : "Adicionar"}
          </button>
        </footer>
      </div>
    </div>
  );
}
